import { useMemo, useState } from "react";

import {
  useExternalProviders,
  useOrderMarkets,
  useRateConfigs,
  useSessionUser,
  useWorkspaceConnections,
} from "@web-app/api";
import { Avatar, Icon, LoadingIcon } from "@web-app/components";
import { ExternalProviderKind, RateOrderType, RateType } from "@web-app/types";

import { externalProviderDisplay, formatCurrency } from "../utils";

type SelectProvidersStepProps = {
  onExternalChange: (providers: ExternalProviderKind[]) => void;
  onInternalChange: (dspWorkspaceIds: string[]) => void;
  selectedDspIds: string[];
  selectedExternalProviders: ExternalProviderKind[];
};

const VISIBLE_LIMIT = 6;

const TYPE_LABEL: Record<RateType, string> = {
  PER_HOUR: "per hr",
  PER_MILE: "per mile",
  PER_ORDER: "per drop",
};

const orderTypesLabel = (orderTypes: RateOrderType[]) => {
  const hasP2P = orderTypes.includes("POINT_TO_POINT");
  const hasRouted = orderTypes.includes("ROUTED");
  if (hasP2P && hasRouted) return "P2P & Routed";
  if (hasRouted) return "Routed";
  return "P2P";
};

export default function SelectProvidersStep({
  onExternalChange,
  onInternalChange,
  selectedDspIds,
  selectedExternalProviders,
}: SelectProvidersStepProps) {
  const { data: sessionUser } = useSessionUser();
  const { data: rateConfigs, isLoading } = useRateConfigs();
  const { data: connections } = useWorkspaceConnections();
  const { data: marketsMap } = useOrderMarkets();
  const { data: externalProviders, isLoading: isLoadingExternal } =
    useExternalProviders();

  const currentWorkspaceId = sessionUser?.currentWorkspaceId || "";

  const stateNameById = useMemo(() => {
    const map = new Map<string, string>();
    Array.from(marketsMap?.values() || []).forEach((state) =>
      map.set(state.id, state.name),
    );
    return map;
  }, [marketsMap]);

  const statesLabel = (stateIds: string[]) =>
    stateIds.length === 0
      ? "All states"
      : stateIds.map((id) => stateNameById.get(id) || id).join(", ");

  const providerNameById = useMemo(() => {
    const map = new Map<string, { avatarUrl: string; name: string }>();
    (connections || []).forEach((connection) => {
      map.set(connection.connectedWorkspaceId, {
        avatarUrl: connection.connectedWorkspace.avatarUrl || "",
        name: connection.connectedWorkspace.name,
      });
    });
    return map;
  }, [connections]);

  const internalProviders = useMemo(
    () =>
      (rateConfigs || []).filter(
        (config) =>
          config.connectedWorkspaceId === currentWorkspaceId && config.isActive,
      ),
    [rateConfigs, currentWorkspaceId],
  );

  const [showAll, setShowAll] = useState(false);

  const toggleInternal = (workspaceId: string) => {
    if (selectedDspIds.includes(workspaceId)) {
      onInternalChange(selectedDspIds.filter((id) => id !== workspaceId));
    } else {
      onInternalChange([...selectedDspIds, workspaceId]);
    }
  };

  const allInternalSelected =
    internalProviders.length > 0 &&
    internalProviders.every((config) =>
      selectedDspIds.includes(config.workspaceId),
    );

  const toggleAllInternal = () => {
    onInternalChange(
      allInternalSelected
        ? []
        : internalProviders.map((config) => config.workspaceId),
    );
  };

  const toggleExternal = (kind: ExternalProviderKind) => {
    if (selectedExternalProviders.includes(kind)) {
      onExternalChange(selectedExternalProviders.filter((k) => k !== kind));
    } else {
      onExternalChange([...selectedExternalProviders, kind]);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <LoadingIcon className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const visibleInternal =
    showAll || internalProviders.length <= VISIBLE_LIMIT
      ? internalProviders
      : internalProviders.slice(0, VISIBLE_LIMIT);
  const externals = externalProviders || [];

  const allExternalSelected =
    externals.length > 0 &&
    externals.every((p) => selectedExternalProviders.includes(p.kind));

  const toggleAllExternal = () => {
    onExternalChange(allExternalSelected ? [] : externals.map((p) => p.kind));
  };

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">
            Internal delivery service providers
          </h3>
          <div className="flex items-center gap-3">
            {internalProviders.length > 0 && (
              <button
                type="button"
                onClick={toggleAllInternal}
                className="text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                {allInternalSelected ? "Clear all" : "Select all"}
              </button>
            )}
            <span className="text-sm text-gray-500">
              Selected ({selectedDspIds.length})
            </span>
            {internalProviders.length > VISIBLE_LIMIT && (
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                {showAll ? "Show less" : "See all providers"}
              </button>
            )}
          </div>
        </div>

        {internalProviders.length === 0 ? (
          <p className="py-12 text-center text-sm text-gray-500">
            No active providers have set rates for your workspace yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visibleInternal.map((config) => {
              const provider = providerNameById.get(config.workspaceId);
              const isSelected = selectedDspIds.includes(config.workspaceId);
              return (
                <button
                  key={config.id}
                  type="button"
                  onClick={() => toggleInternal(config.workspaceId)}
                  className="rounded-lg border border-gray-200 p-4 text-left hover:border-gray-400"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Avatar
                        name={provider?.name || ""}
                        image={provider?.avatarUrl || ""}
                        size={8}
                      />
                      <span className="font-medium text-gray-900">
                        {provider?.name || "Provider"}
                      </span>
                    </div>
                    <Icon
                      name={isSelected ? "circleCheck" : "circle"}
                      className={
                        isSelected
                          ? "h-5 w-5 text-gray-900"
                          : "h-5 w-5 text-gray-300"
                      }
                    />
                  </div>
                  <div className="mt-3 space-y-2">
                    {config.rates.length === 0 ? (
                      <p className="text-sm text-gray-400">No rates set</p>
                    ) : (
                      config.rates.map((rate) => (
                        <div key={rate.id} className="text-sm">
                          <div className="flex items-center gap-2 text-gray-900">
                            <span className="font-medium">
                              {formatCurrency(rate.amount, rate.currency)}
                            </span>
                            <span className="text-gray-400">
                              {TYPE_LABEL[rate.type]}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {orderTypesLabel(rate.orderTypes)} ·{" "}
                            {statesLabel(rate.stateIds)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {(isLoadingExternal || externals.length > 0) && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">
              External delivery service providers
            </h3>
            <div className="flex items-center gap-3">
              {externals.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAllExternal}
                  className="text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  {allExternalSelected ? "Clear all" : "Select all"}
                </button>
              )}
              <span className="text-sm text-gray-500">
                Selected ({selectedExternalProviders.length})
              </span>
            </div>
          </div>
          {isLoadingExternal && (
            <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 py-8 text-sm text-gray-500">
              <LoadingIcon className="h-4 w-4 animate-spin text-gray-400" />
              Loading external providers
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {externals.map((p) => {
              const isSelected = selectedExternalProviders.includes(p.kind);
              return (
                <button
                  key={p.kind}
                  type="button"
                  onClick={() => toggleExternal(p.kind)}
                  className="flex items-center justify-between rounded-lg border border-gray-200 p-4 text-left hover:border-gray-400"
                >
                  <div className="flex items-center gap-2">
                    <Avatar
                      name={p.label}
                      image={externalProviderDisplay(p.kind).avatarUrl}
                      size={8}
                    />
                    <span className="font-medium text-gray-900">{p.label}</span>
                  </div>
                  <Icon
                    name={isSelected ? "circleCheck" : "circle"}
                    className={
                      isSelected
                        ? "h-5 w-5 text-gray-900"
                        : "h-5 w-5 text-gray-300"
                    }
                  />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
